import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Input,
  CircularProgress,
  Button,
  Sheet,
  Table,
  Checkbox,
} from '@mui/joy';
import { ChevronLeft, Key } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { canEditUtenti } from '../../utils/permissions';

// Import react-toastify
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function DettaglioUtente() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permessi, setPermessi] = useState({});

  const selectedOrg = localStorage.getItem('selectedOrg');

  const livelli = ['Nessun accesso', 'Visualizzatore', 'Editor'];

  const menuLabels = {
    2: 'Categorie',
    3: 'Mezzi',
    4: 'Utenti',
    5: 'Checklist',
    8: 'Organizzazione',
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}`,
          {
            credentials: 'include',
          }
        );
        if (!res.ok) throw new Error('Errore fetch utente');

        const data = await res.json();
        setUser(data);

        if (data.permessi_json) {
          const parsed = JSON.parse(data.permessi_json);
          const map = {};
          parsed.forEach((perm) => {
            map[perm.menu_id] = perm.tipo;
          });
          setPermessi(map);
        }
      } catch (err) {
        console.error('Errore nel fetch utente:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [selectedOrg, id]);

  const handlePermessoChange = (menuId, tipo) => {
    setPermessi((prev) => ({
      ...prev,
      [menuId]: tipo,
    }));
  };

  const handleResetPassword = async () => {
    if (!user || !selectedOrg) return;
  
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}/reset-password`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
  
      if (!res.ok) throw new Error('Errore nel reset della password');
  
      toast.success('Password resettata con successo', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Errore reset password:', err);
      toast.error('Errore nel reset della password', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };  

  const handleToggleStatus = async () => {
    if (!user || !selectedOrg) return;
  
    const nuovoStatus = user.user_status === 1 ? 0 : 1;
  
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            status: nuovoStatus,
            user_id: parseInt(id, 10),
            org_id: parseInt(selectedOrg, 10),
          }),
        }
      );
  
      if (!res.ok) throw new Error('Errore nella modifica dello stato utente');
  
      // Aggiorna localmente lo stato utente per riflettere il cambiamento
      setUser((prev) => ({
        ...prev,
        user_status: nuovoStatus,
      }));
  
      toast.success(
        nuovoStatus === 1 ? 'Utente attivato con successo' : 'Utente disattivato con successo',
        { position: 'top-right', autoClose: 3000 }
      );
    } catch (err) {
      console.error('Errore toggle stato utente:', err);
      toast.error('Errore nel cambio di stato utente', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };
  

  const handleSavePermessi = async () => {
    if (!user || !selectedOrg) return;

    const permessiArray = Object.entries(permessi).map(([menu_id, tipo]) => ({
      menu_id: parseInt(menu_id, 10),
      tipo,
    }));

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}/permessi`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            permessi_json: permessiArray,
          }),
        }
      );

      if (!res.ok) throw new Error('Errore nel salvataggio dei permessi');

      // Mostra toast di successo
      toast.success('Permessi aggiornati', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Errore aggiornamento permessi:', err);
      // Mostra toast di errore
      toast.error('Errore nel salvataggio dei permessi', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  return (
        <Box>
          <Typography level="h3" mb={1}>
            Dettaglio Utente
          </Typography>

          <Button
            startDecorator={<ChevronLeft />}
            onClick={() => navigate(-1)}
            variant="outlined"
            size="sm"
            sx={{ mb: 3 }}
          >
            Indietro
          </Button>

          {loading ? (
            <CircularProgress />
          ) : !user ? (
            <Typography color="danger">Utente non trovato.</Typography>
          ) : (
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4}>
              {/* DATI UTENTE */}
              <Sheet
                variant="outlined"
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: 'md',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  minWidth: 280,
                }}
              >
                {/* Stato utente */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: user.user_status === 1 ? 'success.500' : 'danger.500',
                    }}
                  />
                  <Typography
                    level="body-md"
                    sx={{ color: user.user_status === 1 ? 'success.600' : 'danger.600' }}
                  >
                    {user.user_status === 1 ? 'Attivo' : 'Disattivo'}
                  </Typography>
                </Box>
                <Input
                  disabled
                  value={user.nome || ''}
                  placeholder="Nome"
                  startDecorator={<Typography level="body-sm">Nome</Typography>}
                />
                <Input
                  disabled
                  value={user.username || ''}
                  placeholder="Username"
                  startDecorator={<Typography level="body-sm">Username</Typography>}
                />
                <Input
                  disabled
                  value={user.codice_fiscale || ''}
                  placeholder="Codice Fiscale"
                  startDecorator={<Typography level="body-sm">CF</Typography>}
                />

                {/* Pulsante attiva/disattiva */}
                {canEditUtenti() && (
                  <Button
                    variant="solid"
                    color={user.user_status === 1 ? 'danger' : 'success'}
                    sx={{ mt: 1 }}
                    onClick={handleToggleStatus}
                  >
                    {user.user_status === 1 ? 'Disattiva' : 'Attiva'}
                  </Button>
                )}

                {canEditUtenti() && (
                  <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<Key />}
                    sx={{ mt: 2 }}
                    onClick={handleResetPassword}
                  >
                    Reset Password
                  </Button>
                )}
              </Sheet>

              {/* PERMESSI */}
              <Sheet
                variant="outlined"
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: 'md',
                  minWidth: 300,
                  overflowX: 'auto',
                }}
              >
                <Typography level="title-md" mb={2}>
                  Permessi
                </Typography>

                <Table size="sm" borderAxis="both">
                  <thead>
                    <tr>
                      <th>Sezione</th>
                      {livelli.map((liv, i) => (
                        <th key={i} style={{ textAlign: 'center' }}>
                          {liv}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(menuLabels).map((menuId) => {
                      const id = parseInt(menuId, 10);
                      const tipo = permessi[id] ?? 0;

                      return (
                        <tr key={id}>
                          <td>{menuLabels[id]}</td>
                          {livelli.map((_, livello) => (
                            <td key={livello} style={{ textAlign: 'center' }}>
                              <Checkbox
                                checked={tipo === livello}
                                onChange={() => handlePermessoChange(id, livello)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                {canEditUtenti() && (
                  <Button onClick={handleSavePermessi} sx={{ mt: 2 }} color="primary">
                    Salva Permessi
                  </Button>
                )}
              </Sheet>
            </Box>
          )}

      {/* Toast Container per react-toastify */}
      <ToastContainer />
    </Box>
  );
}
